import { CheckCircle2, ChevronLeft, ChevronRight, Palette, XCircle } from "lucide-react";

import type { IPro014CampaignItem, IPro014ItemsPage } from "@/@types/campaign";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { PAGINATION_LABELS } from "@/constants/pagination";

import {
  GRADE_BUTTON_ARIA,
  GRADE_BUTTON_LABEL,
  GRADE_BUTTON_TITLE,
  gradeCountLabel,
} from "../../components/grade/grade-messages";
import {
  PRODUCTS_FILTER_KEY,
  PRODUCTS_PAGE_SIZES,
  PRODUCTS_TEXTS,
  SKELETON_ROWS,
} from "../constants";
import type { GradeItem, ProductsActionsState, ProductsFilterKey } from "../types";
import { COMMON_TEXTS } from "../../utils/messages";

const HEADER_CLASS = "px-4 py-3 text-left text-sm font-semibold";
const CELL_CLASS = "px-4 py-3";

interface ProductsTableProps {
  itemsPage?: IPro014ItemsPage;
  isLoading: boolean;
  showStoreColumn: boolean;
  page: number;
  limit: number;
  actions: ProductsActionsState;
  onEditGrade: (item: GradeItem) => void;
  onFilterChange: (key: ProductsFilterKey, value: string) => void;
}

interface ProductRowProps {
  item: IPro014CampaignItem;
  showStoreColumn: boolean;
  actions: ProductsActionsState;
  onEditGrade: (item: GradeItem) => void;
}

function ProductRow({
  item,
  showStoreColumn,
  actions,
  onEditGrade,
}: ProductRowProps): JSX.Element {
  const toggleLabel: string = item.isActive ? PRODUCTS_TEXTS.DEACTIVATE : PRODUCTS_TEXTS.ACTIVATE;

  return (
    <tr className="hover:bg-gray-50 dark:hover:bg-gray-900/50">
      <td className={CELL_CLASS}>
        <Checkbox
          checked={actions.isSelected(item)}
          onCheckedChange={() => actions.toggleSelection(item)}
          aria-label={PRODUCTS_TEXTS.SELECT_ITEM(item.itemCode)}
        />
      </td>
      {showStoreColumn && <td className={`${CELL_CLASS} font-mono text-sm`}>{item.storeCode}</td>}
      <td className={`${CELL_CLASS} font-mono text-sm`}>{item.itemCode}</td>
      <td className={`${CELL_CLASS} text-sm`}>{item.itemName?.trim() || PRODUCTS_TEXTS.EMPTY_NAME}</td>
      <td className={CELL_CLASS}>
        <Badge variant={item.isActive ? "default" : "secondary"}>
          {item.isActive ? PRODUCTS_TEXTS.ACTIVE_BADGE : PRODUCTS_TEXTS.INACTIVE_BADGE}
        </Badge>
      </td>
      <td className={CELL_CLASS}>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-7 gap-1"
            title={GRADE_BUTTON_TITLE}
            aria-label={GRADE_BUTTON_ARIA(item.itemCode)}
            onClick={() => onEditGrade({ itemCode: item.itemCode, itemName: item.itemName })}
          >
            <Palette className="h-3.5 w-3.5" aria-hidden />
            {GRADE_BUTTON_LABEL}
          </Button>
          {(item.grades?.length ?? 0) > 0 && (
            <Badge variant="secondary" className="text-[10px] font-normal">
              {gradeCountLabel(item.grades)}
            </Badge>
          )}
        </div>
      </td>
      <td className={CELL_CLASS}>
        <Button
          variant={item.isActive ? "outline" : "default"}
          size="sm"
          onClick={() => void actions.toggleItem(item)}
          disabled={actions.isToggling}
        >
          {item.isActive && <XCircle className="mr-2 h-4 w-4" aria-hidden />}
          {!item.isActive && <CheckCircle2 className="mr-2 h-4 w-4" aria-hidden />}
          {toggleLabel}
        </Button>
      </td>
    </tr>
  );
}

export function ProductsTable({
  itemsPage,
  isLoading,
  showStoreColumn,
  page,
  limit,
  actions,
  onEditGrade,
  onFilterChange,
}: ProductsTableProps): JSX.Element {
  const items: IPro014CampaignItem[] = itemsPage?.items ?? [];
  const allSelected: boolean = items.length > 0 && actions.selectedItems.length === items.length;

  if (isLoading) {
    return (
      <div className="overflow-x-auto rounded-lg border bg-white shadow dark:bg-gray-950">
        <div className="p-8">
          {Array.from({ length: SKELETON_ROWS }).map((_value: unknown, index: number) => (
            <Skeleton key={index} className="mb-2 h-12" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border bg-white shadow dark:bg-gray-950">
      <table className="w-full">
        <thead className="bg-gray-50 dark:bg-gray-900">
          <tr>
            <th className="w-12 px-4 py-3 text-left">
              <Checkbox
                checked={allSelected}
                onCheckedChange={() => actions.toggleAll(itemsPage)}
                aria-label={PRODUCTS_TEXTS.SELECT_ALL}
              />
            </th>
            {showStoreColumn && <th className={HEADER_CLASS}>{PRODUCTS_TEXTS.COLUMN_STORE}</th>}
            <th className={HEADER_CLASS}>{PRODUCTS_TEXTS.COLUMN_CODE}</th>
            <th className={HEADER_CLASS}>{PRODUCTS_TEXTS.COLUMN_NAME}</th>
            <th className={HEADER_CLASS}>{PRODUCTS_TEXTS.COLUMN_STATUS}</th>
            <th className={HEADER_CLASS}>{PRODUCTS_TEXTS.COLUMN_GRADE}</th>
            <th className={HEADER_CLASS}>{PRODUCTS_TEXTS.COLUMN_ACTIONS}</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {items.map((item: IPro014CampaignItem) => (
            <ProductRow
              key={`${item.itemCode}-${item.storeCode}`}
              item={item}
              showStoreColumn={showStoreColumn}
              actions={actions}
              onEditGrade={onEditGrade}
            />
          ))}
        </tbody>
      </table>

      {itemsPage && itemsPage.totalPages > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-t px-4 py-3">
          <div className="text-sm text-muted-foreground">
            {COMMON_TEXTS.SHOWING(
              (page - 1) * limit + 1,
              Math.min(page * limit, itemsPage.total),
              itemsPage.total,
            )}
          </div>
          <div className="flex items-center gap-2">
            <Select
              value={limit.toString()}
              onValueChange={(value: string) => onFilterChange(PRODUCTS_FILTER_KEY.LIMIT, value)}
            >
              <SelectTrigger className="w-20" aria-label={PRODUCTS_TEXTS.PAGE_SIZE}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRODUCTS_PAGE_SIZES.map((size: number) => (
                  <SelectItem key={size} value={size.toString()}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onFilterChange(PRODUCTS_FILTER_KEY.PAGE, (page - 1).toString())}
              disabled={page === 1}
              aria-label={PAGINATION_LABELS.PREVIOUS}
            >
              <ChevronLeft className="h-4 w-4" aria-hidden />
            </Button>
            <span className="text-sm">{COMMON_TEXTS.PAGE(page, itemsPage.totalPages)}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onFilterChange(PRODUCTS_FILTER_KEY.PAGE, (page + 1).toString())}
              disabled={page === itemsPage.totalPages}
              aria-label={PAGINATION_LABELS.NEXT}
            >
              <ChevronRight className="h-4 w-4" aria-hidden />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
